import { useSnackbar } from '@components/snackbar';
import { AutomationIcon, ChevronDownIcon } from '@sketchnote/icons';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import Select from 'react-select';
import { API } from 'src/api';
import { useKanbanStore } from 'src/features/kanban';

import { SubtaskCreation } from './create-subtask';

export const FileAutomation = () => {
  const router = useRouter();
  const { columns } = useKanbanStore();
  const boardColumns = columns.map((column) => ({
    value: column.id,
    label: column.name,
  }));
  const [automationData, setAutomationData] = useState<any>([]);
  const eventsOptions =
    automationData &&
    automationData.events &&
    automationData.events
      .filter((item) => item.active)
      .map((item) => ({ value: item.id, label: item.text }));
  const actionOptions =
    automationData &&
    automationData.actions &&
    automationData.actions
      .filter((item) => item.active)
      .map((item) => ({
        value: item.type,
        label: item.text,
      }));

  const [selectedEvent, setSelectedEvent] = useState<any>();
  const [selectedAction, setSelectedAction] = useState<any>();
  const [subtasks, setSubtasks] = useState<string[]>([]);
  const [loaderIsOpen, setLoaderIsOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const [event, setEvent] = useState('');
  const [action, setAction] = useState('');
  const [payload, setPayload] = useState<any>({});

  useEffect(() => {
    payload.names = subtasks;
  }, [subtasks]);

  const boardId = router.query.view as string;
  const [openSnackbar] = useSnackbar();

  const handleCreateAutomation = async () => {
    if (event.length === 0 || action.length === 0) {
      openSnackbar({
        text: 'select automation and action',
        duration: 1500,
        snackType: 'error',
        position: 'bottom-center',
      });
    } else {
      const response = await API.post('/board/automation', payload);
      if (response.data.success) {
        setLoaderIsOpen(false);
        openSnackbar({
          text: 'Automation created successfully',
          snackType: 'standard',
          position: 'bottom-center',
          duration: 1500,
        });
      } else {
        setLoaderIsOpen(false);
        openSnackbar({
          text: response.data.message,
          snackType: 'standard',
          position: 'bottom-center',
          duration: 1500,
        });
      }
    }
    setIsDisabled(false);
    setSelectedAction('');
    setSelectedEvent('');
    setEvent('');
    setAction('');
  };

  const handleSelectEvent = (e) => {
    const event = automationData.events.find((item) => item.id === e.value);
    setEvent(event.type);
    setPayload({});
    setPayload((prev) => {
      prev.event = event.type;
      return { ...prev };
    });
    setSelectedEvent(e);
    setSelectedAction('');
  };

  const handleSelectAction = (e) => {
    const action = automationData.actions.find((item) => item.type === e.value);
    setAction(action.type);
    setPayload((prev) => {
      prev.title = e.label;
      prev.id = boardId;
      prev.action = action.type;
      return { ...prev };
    });
    setSelectedAction(e);
  };

  const handleToColumn = (e) => {
    setPayload((prev) => {
      prev.to = e.value;
      return { ...prev };
    });
  };

  const handleFromColumn = (e) => {
    setPayload((prev) => {
      prev.from = e.value;
      return { ...prev };
    });
  };

  const GetAllAutomations = async () => {
    setLoaderIsOpen(true);
    const response = await API.get('/board/automation?query=1');
    if (response) {
      setLoaderIsOpen(false);
      setAutomationData(response.data.data);
    }
  };

  useEffect(() => {
    GetAllAutomations();
  }, []);

  return (
    <div className="flex flex-col justify-between h-full">
      <div className="flex">
        <section className="flex flex-col w-1/2 bg-gray-100 mr-10 p-15 rounded-lg h-425">
          <span className="bg-white w-full mb-24 p-8 rounded-lg border">
            <AutomationIcon size={15} className="flex mr-4 items-center" fill={'#254DDA'} />
            When
            <p className="text-gray-500 font-regular text-xs">
              this happens on a task or a subtask
            </p>
          </span>
          <div className="flex items-center">
            <span className="flex flex-col w-300 bg-white border rounded-lg p-10">
              <Select
                options={eventsOptions}
                value={selectedEvent}
                placeholder="Select an automation"
                onChange={(e) => handleSelectEvent(e)}
                styles={{
                  control: (styles) => ({
                    ...styles,
                    backgroundColor: '#F6F7F9',
                    paddingLeft: '12px',
                    paddingRight: '12px',
                    borderRadius: '6px',
                    borderColor: '#E2E4E9',
                    fontWeight: 400,
                    border: 0,
                    fontSize: '1.4rem',
                    color: '#131720',
                  }),
                  menu: (styles) => ({ ...styles, fontWeight: 400, fontSize: '1.4rem' }),
                }}
                components={{
                  DropdownIndicator: ChevronDownIcon as any,
                  IndicatorSeparator: () => null,
                }}
              />
              {selectedEvent && selectedEvent.value !== 'task_created' && (
                <span className="flex text-sm font-regular text-gray-600 pl-22 mt-10 items-center ">
                  From:{' '}
                  <Select
                    options={boardColumns}
                    placeholder=""
                    onChange={handleFromColumn}
                    styles={{
                      control: (styles) => ({
                        ...styles,
                        width: '206px',
                        backgroundColor: '#fff',
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        fontWeight: 400,
                        fontSize: '1.4rem',
                        border: 0,
                        color: '#131720',
                        marginLeft: '12px',
                      }),
                      menu: (styles) => ({
                        ...styles,
                        fontWeight: 400,
                        fontSize: '1.4rem',
                        color: '#131720',
                      }),
                    }}
                    components={{
                      DropdownIndicator: ChevronDownIcon as any,
                      IndicatorSeparator: () => null,
                    }}
                  />
                </span>
              )}
              {selectedEvent && selectedEvent.value !== 'task_created' && (
                <div className="flex text-sm font-regular text-gray-600 pl-22 mt-10 items-center">
                  To:{' '}
                  <Select
                    options={boardColumns}
                    placeholder=""
                    onChange={handleToColumn}
                    styles={{
                      control: (styles) => ({
                        ...styles,
                        backgroundColor: '#fff',
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        fontWeight: 400,
                        width: '206px',
                        fontSize: '1.4rem',
                        border: 0,
                        color: '#131720',
                        marginLeft: '30px',
                      }),
                      menu: (styles) => ({
                        ...styles,
                        fontWeight: 400,
                        fontSize: '1.4rem',
                        color: '#131720',
                      }),
                    }}
                    components={{
                      DropdownIndicator: ChevronDownIcon as any,
                      IndicatorSeparator: () => null,
                    }}
                  />
                </div>
              )}
            </span>
          </div>
        </section>
        <section className="flex flex-col w-1/2 bg-gray-100 p-15 rounded-lg h-425">
          <span className="bg-white w-full mb-24 p-8 rounded-lg border text-sm">
            <AutomationIcon size={15} className="flex mr-4 items-center" fill={'#F87463'} />
            Then
            <p className="text-gray-500 font-regular text-xs">perform this action</p>
          </span>
          <div className="flex items-center">
            <span className="flex flex-col w-300 bg-white border rounded-lg p-10">
              <Select
                options={actionOptions}
                placeholder="Select an action"
                value={selectedAction}
                onChange={(e) => handleSelectAction(e)}
                styles={{
                  control: (styles) => ({
                    ...styles,
                    backgroundColor: '#F6F7F9',
                    paddingLeft: '12px',
                    paddingRight: '12px',
                    borderRadius: '6px',
                    borderColor: '#E2E4E9',
                    fontWeight: 400,
                    fontSize: '1.4rem',
                    color: '#131720',
                    border: 0,
                    marginBottom: '',
                  }),
                  menu: (styles) => ({ ...styles, fontWeight: 400, fontSize: '1.4rem' }),
                }}
                components={{
                  DropdownIndicator: ChevronDownIcon as any,
                  IndicatorSeparator: () => null,
                }}
              />
              <span className="flex">
                {selectedAction && selectedAction.value === '.create.subtask' && (
                  <SubtaskCreation subtasks={subtasks} setSubtasks={setSubtasks} />
                )}
              </span>
            </span>
          </div>
        </section>
      </div>
      <button
        className={classNames('flex px-4 py-10 text-white rounded w-244', {
          'bg-gray-200': isDisabled,
          'bg-primary-500': !isDisabled,
        })}
        onClick={() => {
          setIsDisabled(true);
          handleCreateAutomation();
        }}
        disabled={isDisabled}
      >
        <span className="flex justify-center w-full mx-8 font-sans font-medium text-md">
          Create automation
        </span>
      </button>
    </div>
  );
};
